import { Link } from 'react-router-dom';
import PageTemplate from '../../../components/feature/PageTemplate';
import SharedProcessSection from '../../../components/feature/SharedProcessSection';
import SharedFAQSection from '../../../components/feature/SharedFAQSection';

function Hero() {
  return (
    <section className="relative min-h-[70vh] flex flex-col justify-end overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/0d560b7cec6350cf32c291808d11e0c8.jpg"
          alt="Storm damage roof restoration Utah"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/50 to-charcoal/20"></div>
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-6 pb-16 pt-36 w-full">
        <div className="flex items-center gap-2 text-white/50 font-body text-xs mb-4">
          <Link to="/" className="hover:text-white cursor-pointer">Home</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-white/80">Storm Damage</span>
        </div>
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 bg-brand-orange text-white text-xs font-700 uppercase tracking-widest px-4 py-1.5 rounded-full mb-5 font-body">
            <i className="ri-cloud-windy-fill"></i> Emergency Response Available
          </div>
          <h1 className="font-heading font-900 text-white text-5xl md:text-7xl lg:text-8xl uppercase tracking-tight leading-none mb-4">
            Storm Damage Restoration<br />
            <span className="text-brand-orange">&amp; Insurance Claims</span>
          </h1>
          <p className="font-body text-white/70 text-lg mb-8 max-w-2xl leading-relaxed">
            Fast response to hail and wind damage across Utah. We document everything, fight for your full insurance settlement, and restore your roof to like-new condition.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/#estimate" className="flex items-center gap-2 bg-brand-orange hover:bg-brand-orange-dark text-white font-heading font-800 text-base uppercase tracking-widest px-8 py-3.5 rounded-md cursor-pointer whitespace-nowrap transition-colors">
              <i className="ri-search-eye-fill"></i> Request Damage Inspection
            </Link>
            <a href="tel:+18015550199" className="flex items-center gap-2 border border-white/30 hover:border-white text-white font-heading font-800 text-base uppercase tracking-widest px-8 py-3.5 rounded-md cursor-pointer whitespace-nowrap transition-colors">
              <i className="ri-phone-fill text-brand-orange"></i> Emergency Line
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function MainSection() {
  const SIGNS = [
    { icon: 'ri-circle-fill', title: 'Hail Impact Marks', desc: 'Circular dents or bruising on shingles, metal flashing, gutters, and vents — often invisible from the ground.' },
    { icon: 'ri-wind-fill', title: 'Missing or Lifted Shingles', desc: 'Utah\'s Wasatch Front wind events regularly lift and displace shingles, exposing vulnerable decking.' },
    { icon: 'ri-drop-fill', title: 'Interior Water Stains', desc: 'Ceiling discoloration, sagging drywall, or wet insulation indicates existing storm damage with active infiltration.' },
    { icon: 'ri-flashlight-fill', title: 'Damaged Flashing', desc: 'Lifted, bent, or separated flashing around chimneys, vents, and skylights — a primary post-storm leak source.' },
    { icon: 'ri-bar-chart-2-fill', title: 'Granule Loss', desc: 'Excessive shingle granules accumulating in gutters signal impact damage and accelerated shingle degradation.' },
    { icon: 'ri-leaf-fill', title: 'Debris Damage', desc: 'Branches, debris, or falling objects may cause punctures, cracks, or structural damage during severe Utah storms.' },
  ];

  return (
    <section className="py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-14">
          <div className="inline-block w-16 h-1 bg-brand-orange rounded-full mb-6"></div>
          <h2 className="font-heading font-900 text-charcoal text-5xl md:text-6xl uppercase tracking-tight leading-none mb-4">
            Signs of Storm <span className="text-brand-orange">Roof Damage</span>
          </h2>
          <p className="font-body text-charcoal/60 text-lg max-w-xl mx-auto">After any Utah hailstorm or windstorm, check for these warning signs. Many are only visible from the roof.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-14">
          {SIGNS.map((s) => (
            <div key={s.title} className="flex items-start gap-4 bg-surface hover:bg-surface-dark transition-colors rounded-2xl p-6">
              <div className="w-12 h-12 flex items-center justify-center bg-brand-orange/10 rounded-lg flex-shrink-0">
                <i className={`${s.icon} text-brand-orange text-2xl`}></i>
              </div>
              <div>
                <h3 className="font-heading font-900 text-charcoal text-xl uppercase tracking-wide mb-1">{s.title}</h3>
                <p className="font-body text-charcoal/60 text-sm leading-relaxed">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="bg-charcoal rounded-2xl p-8 text-center">
          <h3 className="font-heading font-900 text-white text-3xl uppercase tracking-wide mb-3">Not Sure If You Have Damage?</h3>
          <p className="font-body text-white/60 text-base mb-6">We provide free post-storm inspections throughout Utah. Our trained inspectors identify damage you can&apos;t see from the ground.</p>
          <Link to="/#estimate" className="inline-flex items-center gap-2 bg-brand-orange hover:bg-brand-orange-dark text-white font-heading font-800 text-base uppercase tracking-widest px-8 py-3.5 rounded-md cursor-pointer whitespace-nowrap transition-colors">
            <i className="ri-search-eye-fill"></i> Book Free Inspection
          </Link>
        </div>
      </div>
    </section>
  );
}

function AIBlock() {
  return (
    <section className="py-28 bg-charcoal">
      <div className="max-w-5xl mx-auto px-6">
        <div className="bg-charcoal-light rounded-2xl p-10 border-l-4 border-brand-orange">
          <h2 className="font-heading font-900 text-white text-4xl md:text-5xl uppercase tracking-tight mb-6">
            Storm Damage &amp; <span className="text-brand-orange">Insurance Claims</span>
          </h2>
          <p className="font-body text-white/70 text-lg leading-relaxed mb-8">
            <strong className="text-white">IWC Roofing Utah</strong> specializes in storm damage assessment and insurance claim management for Utah homeowners. After hail or wind events, we provide rapid response, professional documentation, and direct advocacy with your insurance company — ensuring you receive full entitled coverage for qualifying damage.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: 'What We Document', icon: 'ri-camera-fill', items: ['Hail impact measurements', 'All damaged areas mapped', 'Photo & video evidence', 'Replacement cost estimates', 'Insurance-format reports', 'Pre-storm comparison'] },
              { title: 'Our Insurance Advantage', icon: 'ri-file-shield-2-fill', items: ['We attend adjuster meetings', 'Know insurance code language', 'Identify all covered damage', 'Handle supplement claims', 'Dispute low-ball settlements', 'Maximize your payout'] },
              { title: 'Common Claim Outcomes', icon: 'ri-money-dollar-circle-fill', items: ['Full roof replacement approved', 'Partial repair coverage', 'Gutter & flashing included', 'Interior damage included', 'ACV or RCV settlement', 'Deductible-only cost to you'] },
            ].map((col) => (
              <div key={col.title}>
                <div className="flex items-center gap-2 mb-3">
                  <i className={`${col.icon} text-brand-orange text-xl`}></i>
                  <h3 className="font-heading font-800 text-brand-orange text-sm uppercase tracking-widest">{col.title}</h3>
                </div>
                <ul className="space-y-2">
                  {col.items.map((item) => (
                    <li key={item} className="flex items-start gap-2 font-body text-white/60 text-sm">
                      <i className="ri-arrow-right-s-fill text-brand-orange flex-shrink-0 mt-0.5"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const PROCESS = [
  { icon: 'ri-search-eye-line', title: 'Free Inspection', desc: 'We inspect your roof thoroughly and document all storm damage with photos and measurements.' },
  { icon: 'ri-file-text-line', title: 'Claim Filing', desc: 'We prepare your complete damage package and assist with the insurance claim submission.' },
  { icon: 'ri-team-line', title: 'Adjuster Meeting', desc: 'We meet on-site with your insurance adjuster to ensure all damage is properly identified and included.' },
  { icon: 'ri-check-double-line', title: 'Claim Approval', desc: 'We review the insurance settlement and file supplements if any covered damage was missed or underpaid.' },
  { icon: 'ri-hammer-line', title: 'Repair & Restore', desc: 'Once approved, our crews complete all restoration to manufacturer specifications and Utah code.' },
];

const FAQS = [
  { q: 'How quickly can IWC Roofing respond to storm damage in Utah?', a: 'We offer priority response for storm-damaged Utah roofs, typically within twenty-four hours of initial contact. For active leaks or severe structural damage, we provide emergency tarping services to protect your home immediately while your claim is processed and permanent repairs are scheduled.' },
  { q: 'Will my insurance cover storm damage to my roof?', a: 'Most standard homeowners insurance policies cover roof damage caused by hail, wind, and sudden weather events. Coverage depends on your policy terms, deductible, and the age of your roof. IWC Roofing provides comprehensive documentation to support your claim and help maximize your entitled coverage.' },
  { q: 'What does the insurance claim process look like?', a: 'We inspect your roof, photograph and document all damage, prepare an insurance-compatible estimate, and submit to your carrier. We then meet with your adjuster on-site to ensure everything is captured. Once approved, we schedule repairs and work directly with your insurance company throughout the payment process.' },
  { q: 'What if my insurance claim is denied?', a: 'A denial is not always final. We can help you appeal, provide additional documentation, request a re-inspection with a different adjuster, or connect you with a public adjuster for a formal dispute. We have extensive experience navigating Utah insurance claim disputes with successful outcomes.' },
  { q: 'How do I know if hail damaged my roof?', a: 'Hail damage is often subtle and requires professional inspection to identify. Signs include soft spots or circular bruising on shingles, dents on metal components like gutters and vents, and granule loss in downspouts. After any Utah hailstorm, schedule a free IWC inspection promptly to preserve your insurance claim rights.' },
];

export default function StormDamagePage() {
  return (
    <PageTemplate
      hero={<Hero />}
      mainSection={<MainSection />}
      aiBlock={<AIBlock />}
      processSection={<SharedProcessSection title="Our Claims Process" subtitle="We handle every step — you just get your roof restored." steps={PROCESS} />}
      faqSection={<SharedFAQSection title="Storm Damage Answered" faqs={FAQS} />}
      midCTA1="Storm hit your area? Get a free inspection within 24 hours."
      midCTA2="We fight for your full insurance settlement — every time."
      midCTA3="Utah storm damage experts with hundreds of successful claims."
    />
  );
}
